import { isItemAvailable as isItemAvailableAtDate } from '@tictuk/item-availability'
import type MenuSection from 'types/MenuSection'
import type MenuItem from 'types/MenuItem'
import type { Variation } from 'types/MenuItem'
import type { OrderType } from 'types/OrderType'
import { union } from 'lodash-es'
import type { ChannelType } from 'types/ChannelType'
import { CONSTANTS } from 'utils/constants'

const MAX_RECURSION_DEPTH = 10

interface FilterCache {
	flatQuantityCheckCache: Record<string, boolean>
	nestedQuantityItemCache: Record<string, boolean>
}

const isItemAvailableForChannel = (channelsArray: ChannelType[], channelType: ChannelType): boolean =>
	!channelsArray.length || channelsArray.includes(channelType) || channelType === CONSTANTS.CHANNEL_TYPE.ALL // channelType === 'all' - for static menu
const isItemAvailableForOrderType = (orderTypesArray: OrderType[], orderType: OrderType): boolean =>
	orderType === 'all' || orderTypesArray.includes(orderType)
const isItemAvailable = ({ item, date, channelTypes, orderTypes, channelType, orderType }: any) =>
	isItemAvailableAtDate(item, new Date(date)) &&
	isItemAvailableForChannel(channelTypes, channelType) &&
	isItemAvailableForOrderType(orderTypes, orderType)

export const allChannelTypes = Object.values(CONSTANTS.CHANNEL_TYPE) as ChannelType[]
export const allOrderTypes = Object.values(CONSTANTS.ORDER_TYPE) as OrderType[]

export const filterItemsAndSections = ({
	items,
	sections = [],
	hiddenSections = [],
	date,
	orderType,
	channelType,
	features = {},
}: {
	items: Record<string, MenuItem>
	sections: MenuSection[]
	hiddenSections: MenuSection[]
	date: string
	orderType: OrderType
	channelType: ChannelType
	features?: Record<string, any>
}): {
	filteredItems: Record<string, MenuItem>
	filteredSections: MenuSection[]
} => {
	const allMenuSections = sections.concat(hiddenSections)
	const hiddenSectionsIds = hiddenSections.map((section) => section.id)

	const filteredItems: Record<string, MenuItem> = {}
	const filteredSections: MenuSection[] = []
	const cache: FilterCache = {
		flatQuantityCheckCache: {},
		nestedQuantityItemCache: {},
	}

	if (features.nestedQuantityFiltering) {
		console.log('nestedQuantityFiltering enabled')
	}

	for (const section of allMenuSections) {
		const filteredItemIds = []

		for (const itemId of section.itemIds ?? []) {
			if (items[itemId]) {
				if (features.nestedQuantityFiltering && isItemUsingNestedQuantitySelection(items[itemId], items, cache)) {
					continue
				}

				const mergedChannelTypes = getItemComputedChannelTypes(items[itemId], section.channelTypes)
				const mergedOrderTypes = getItemComputedOrderTypes(items[itemId], section.orderTypes)

				if (
					isItemAvailable({
						item: items[itemId],
						date,
						channelTypes: mergedChannelTypes,
						orderTypes: mergedOrderTypes,
						channelType,
						orderType,
					})
				) {
					filteredItemIds.push(itemId)
					filteredItems[itemId] = items[itemId]
				}
			}
		}
		if (filteredItemIds.length) {
			section.itemIds = filteredItemIds
			if (!hiddenSectionsIds.includes(section.id)) {
				filteredSections.push(section)
			}
		}
	}

	addAvailableItemsNotInSections(items, date, channelType, orderType, filteredItems)

	return { filteredItems, filteredSections }
}

export const addAvailableItemsNotInSections = (
	items: Record<string, MenuItem>,
	date: string,
	channelType: ChannelType,
	orderType: OrderType,
	filteredItems: Record<string, MenuItem>
) => {
	Object.values(items).forEach((item) => {
		if (
			isItemAvailable({
				item,
				date,
				channelTypes: item.description?.channelTypes?.length ? item.description.channelTypes : allChannelTypes,
				orderTypes: item.description?.orderTypes?.length ? item.description.orderTypes : allOrderTypes,
				channelType,
				orderType,
			}) &&
			!item.sectionItem &&
			item.id
		) {
			filteredItems[item.id] = item
		}
	})
}

const getItemComputedChannelTypes = (item: MenuItem, channelTypesFromSection: ChannelType[] = []) => {
	if (!channelTypesFromSection.length && item.description?.channelTypes?.length) {
		return item.description.channelTypes
	}
	return union(channelTypesFromSection?.length ? channelTypesFromSection : allChannelTypes, item.description?.channelTypes)
}

const getItemComputedOrderTypes = (item: MenuItem, orderTypesFromSection: OrderType[] = []) => {
	if (!orderTypesFromSection.length && item.description?.orderTypes?.length) {
		return item.description?.orderTypes
	}
	return union(orderTypesFromSection?.length ? orderTypesFromSection : allOrderTypes, item.description?.orderTypes)
}

const isItemUsingNestedQuantitySelection = (item: MenuItem, items: Record<string, MenuItem>, cache: FilterCache, depth = 0): boolean => {
	const cachedValue = cache.nestedQuantityItemCache[item.id]
	if (cachedValue !== undefined) {
		return cachedValue
	}

	if (!item.variations?.length) {
		cache.nestedQuantityItemCache[item.id] = false
		return false
	}

	const isUsingNestedVariation = item.variations.filter(Boolean).some((variation) => isVariationANestedQuantitySelection(variation, items, cache))

	if (isUsingNestedVariation) {
		cache.nestedQuantityItemCache[item.id] = true
		console.log('nested quantity', item)
		return true
	}

	if (depth > MAX_RECURSION_DEPTH) {
		cache.nestedQuantityItemCache[item.id] = false
		return false
	}

	for (const variation of item.variations) {
		if (!variation.itemIds?.length) {
			continue
		}

		for (const itemId of variation.itemIds) {
			const subItem = itemId && items[itemId]

			if (!subItem || !isItemUsingNestedQuantitySelection(subItem, items, cache, depth + 1)) {
				continue
			}

			cache.nestedQuantityItemCache[item.id] = true
			return true
		}
	}

	cache.nestedQuantityItemCache[item.id] = false
	return false
}

const isVariationANestedQuantitySelection = (variation: Variation, items: Record<string, MenuItem>, cache: FilterCache): boolean => {
	const isQuantitySelection = variation.title?.quantityselection === 'true'

	if (!isQuantitySelection) {
		return false
	}

	return !!variation.itemIds
		?.map((itemId) => items[itemId])
		.filter(Boolean)
		.some((item) => !isItemAFlatQuantitySelectionOption(item, items, cache))
}

const isItemAFlatQuantitySelectionOption = (item: MenuItem, items: Record<string, MenuItem>, cache: FilterCache): boolean => {
	const cachedValue = cache.flatQuantityCheckCache[item.id]
	if (cachedValue !== undefined) {
		return cachedValue
	}

	if (item.variations?.length !== 1) {
		cache.flatQuantityCheckCache[item.id] = false
		return false
	}

	const result = isVariationAFlatQuantitySelection(item.variations[0], items)
	cache.flatQuantityCheckCache[item.id] = result
	return result
}

const isVariationAFlatQuantitySelection = (variation: Variation, items: Record<string, MenuItem>): boolean => {
	if (!variation.itemIds?.length) {
		return false
	}

	return variation.itemIds
		.map((itemId) => items[itemId])
		.filter(Boolean)
		.every(isItemAQuantityItem)
}

const isItemAQuantityItem = (item: MenuItem): boolean => {
	if (!item.title) {
		return false
	}

	const titles = Object.values(item.title).filter(Boolean)

	if (!titles.length) {
		return false
	}

	return titles.every((title) => /^\d+$/.test(title.trim()))
}
